import { FolderApi, TabApi, TabPageApi, Pane, BladeApi } from 'tweakpane';
import * as TextareaPlugin from '@pangenerator/tweakpane-textarea-plugin'; // tslint:disable-line: no-submodule-imports
import Mousetrap from 'mousetrap';
import type { ITweakParams } from '@/typescript/interfaces/ITweakParams';
import type { BladeController, View } from '@tweakpane/core';

type InstanceType = Error | FolderApi | TabPageApi | undefined;

class TweakPanel extends Pane {
    static #instance: InstanceType;
    static isInitialized: boolean = false;

    private constructor(params: ITweakParams) {
        super(params);
        this.registerPlugin(TextareaPlugin);
        this.element.parentElement!.parentElement!.style.bottom = '0';
        this.element.parentElement!.parentElement!.style.overflowY = 'auto';
        this.element.parentElement!.parentElement!.style.overflowX = 'none';
        this.element.parentElement!.parentElement!.style.height = 'fit-content';
    }

    public static init(params: ITweakParams) {
        if (!params) return; // maybe throw an error here
        TweakPanel.#instance = new TweakPanel(params);
        if (TweakPanel.instance instanceof Error) {
            return;
        }
        TweakPanel.isInitialized = true;
        // Bind F2 to toggle panel
        Mousetrap.bind('f2', TweakPanel.togglePanel);
        TweakPanel.instance?.addBinding(
            {
                prop: `Press 'F2' to toggle off the panel\nPress 'F' to toggle fullscreen.`,
            },
            'prop',
            {
                view: 'textarea',
                rows: 4,
                label: '',
                disabled: true,
            },
        );
        TweakPanel.instance!.hidden = true;
    }

    public static getChildrenByLabel(pane: FolderApi, label: string): BladeApi<BladeController<View>> | undefined {
        if (!pane || !label) {
            return undefined;
        }
        // TS api of tweakpane not so well documented couldn't find type for binding as it could be a lot of bindings
        // @ts-expect-error: Property 'label' does not exist on type 'BladeApi<BladeController<View>>'.
        const childrenFound = pane.children.find((child) => child?.label === label);
        return childrenFound;
    }

    /**
     * Dispose keyboard bindings before destroy everything
     */
    public static dispose() {
        const { instance } = TweakPanel;
        if (instance instanceof Error) {
            return;
        }
        Mousetrap.unbind('f2');
        instance?.dispose();
    }

    // can't have access to instance outside Class
    // access tweakPane via getFolderbyName
    private static get instance(): InstanceType {
        if (!TweakPanel.#instance) {
            // could be a promise... or not - should avoid to throw an error but set everything to async
            throw new Error('TweakPanel not initialized');
        }
        return TweakPanel.#instance;
    }

    public static readonly togglePanel = () => {
        if (TweakPanel.instance instanceof Error) {
            return;
        }
        TweakPanel.instance!.hidden = !TweakPanel.instance!.hidden;
    };

    public static getFolderOrTabByName(name?: string, folder: InstanceType = TweakPanel.instance): FolderApi | TabPageApi | undefined {
        if (folder instanceof Error) {
            // Escape if tweakpanel is not defined
            return undefined;
        }
        if (!name || folder?.title === name) {
            return folder;
        }
        let childrenArray: FolderApi[] | TabPageApi[] = [];
        if (folder instanceof TabApi) {
            childrenArray = folder.pages;
        }
        if (folder instanceof FolderApi || folder instanceof TabPageApi) {
            childrenArray = folder.children as FolderApi[] | TabPageApi[];
        }

        const found = childrenArray
            .map((child) => TweakPanel.getFolderOrTabByName(name, child))
            .find((foundFolderOrTab) => foundFolderOrTab instanceof FolderApi || foundFolderOrTab instanceof TabPageApi);

        return found;
    }

    public static getNextTabApi(folder?: InstanceType): TabApi | undefined {
        let refineFolder = folder;
        if (folder === undefined) {
            const { instance } = TweakPanel;
            if (instance instanceof Error) {
                return undefined;
            }
            refineFolder = instance;
        }
        if (refineFolder instanceof TabApi) {
            return refineFolder;
        }
        let childrenArray: FolderApi[] | TabPageApi[] = [];
        if (refineFolder instanceof FolderApi || refineFolder instanceof TabPageApi) {
            childrenArray = refineFolder.children as FolderApi[];
        }
        for (let i = 0; i < childrenArray.length; i++) {
            const child = childrenArray[i];
            const found = TweakPanel.getNextTabApi(child);
            if (found instanceof TabApi) {
                return found;
            }
        }
        return undefined;
    }

    public static removeFolderByName(name: string): Error | void {
        if (!name) {
            return new Error('Folder name not provided');
        }
        const folder = TweakPanel.getFolderOrTabByName(name);
        if (!folder || folder instanceof Error) {
            return folder as Error;
        }
        folder.dispose();
        return undefined;
    }
}

export { TweakPanel };
